import type { ErpSparePart } from '~/types/part'

export function useSparePart() {
  const lastResult = useState<{ sku: string, hits: ErpSparePart[] }>('lastSparePartResult', () => ({ sku: '', hits: [] }))

  /**
   * Spare parts from the Typesense spare parts collection (server route /api/spareparts, cached):
   * "code@supplierId" finds one exact part, a part-code-like query finds parts by (partial) code.
   *
   * Price and stock come from the index (updated from the ERP within a minute), so there is
   * no separate ERP lookup any more. The index only lists parts with an ERP price.
   *
   * A failed search returns null like "not found", unless throwOnError: the spare part page must tell them apart
   * (404 for a missing part, but not for a search outage).
   */
  const getSpareParts = async (sku: string, { throwOnError = false } = {}): Promise<ErpSparePart[] | null> => {
    if (!sku) return null
    if (lastResult.value.sku === sku) {
      return lastResult.value.hits.length > 0 ? lastResult.value.hits : null
    }
    if (sku.endsWith('@')) return null

    const spareParts = await $fetch<ErpSparePart[]>('/api/spareparts', { query: { q: sku } })
      .catch((error) => {
        console.error('Spare part search failed:', error)
        if (throwOnError) throw error
        return null
      })
    if (spareParts === null) return null

    lastResult.value = { sku, hits: spareParts }
    return spareParts.length > 0 ? spareParts : null
  }

  return {
    getSpareParts,
    lastResult
  }
}
